import React from "react";
import { ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import PageHero from "@/components/site/PageHero";
import BottomCTA from "@/components/site/BottomCTA";
import useReveal from "@/hooks/useReveal";

export default function CommunityEducation() {
  useReveal();
  const { t } = useTranslation();
  const pillars = t("impact.pillars", { returnObjects: true });

  return (
    <div className="min-h-screen bg-white text-unik-ink">
      <SiteNav />

      <PageHero
        eyebrow={t("impact.heroEyebrow")}
        title={t("impact.heroTitle")}
        accent={<><br/>{t("impact.heroAccent")}</>}
        bgImage="/assets/unsplash-1528127269322.jpg"
      />

      {/* OVERVIEW */}
      <section className="relative bg-white py-28 lg:py-40">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-20 items-center">
          <div className="lg:col-span-6" data-reveal>
            <img
              src="/assets/unsplash-1731458769726.jpg"
              alt=""
              className="w-full h-[60vh] lg:h-[72vh] object-cover"
            />
          </div>
          <div className="lg:col-span-6">
            <div data-reveal className="eyebrow text-[#14355a]">{t("impact.overviewEyebrow")}</div>
            <h2
              data-reveal
              style={{
                "--reveal-delay": "120ms",
                fontSize: "clamp(2.25rem, 5vw, 4.5rem)",
                fontWeight: 500,
                lineHeight: "1.0",
              }}
              className="mt-6 font-display text-[#14355a] tracking-tight"
            >
              {t("impact.overviewTitle")}<br/>{t("impact.overviewAccent")}
            </h2>
            <div
              data-reveal
              style={{ "--reveal-delay": "240ms" }}
              className="mt-8 space-y-6 max-w-xl text-[18px] lg:text-[19px] leading-[1.95] text-unik-ink/80"
            >
              <p>{t("impact.overviewBody")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CHITTA HEALTHSPAN INSTITUTE */}
      <section className="relative text-white overflow-hidden">
        <div className="absolute inset-0 silk-bg" />
        <div className="absolute inset-0 grain pointer-events-none opacity-[0.04]" />
        <div className="relative z-10 mx-auto max-w-[1400px] px-6 lg:px-10 py-28 lg:py-40">
          <div data-reveal className="max-w-3xl">
            <div className="eyebrow text-unik-gold">{t("impact.instituteEyebrow")}</div>
            <h2
              style={{
                fontSize: "clamp(2.25rem, 5vw, 4.5rem)",
                fontWeight: 500,
                lineHeight: "1.0",
              }}
              className="mt-6 font-display tracking-tight"
            >
              {t("impact.instituteTitle")}<br/>
              <span className="text-unik-gold">{t("impact.instituteAccent")}</span>
            </h2>
            <p className="mt-8 text-[18px] lg:text-[19px] leading-[1.95] text-white/85 max-w-2xl">
              {t("impact.instituteBody")}
            </p>
          </div>

          <div className="mt-16 lg:mt-20 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {pillars.map((c, i) => (
              <div
                key={c.n}
                data-reveal
                style={{ "--reveal-delay": `${i * 130}ms` }}
                className="p-10 lg:p-12 hover:bg-white/[0.04] transition-colors duration-500"
              >
                <div className="font-display text-unik-gold text-xs tracking-[0.3em] font-medium">
                  {c.n}
                </div>
                <h3
                  style={{ fontSize: "clamp(1.375rem, 1.75vw, 1.625rem)", fontWeight: 500 }}
                  className="mt-6 font-display tracking-tight"
                >
                  {c.t}
                </h3>
                <p className="mt-5 text-[15px] leading-[1.85] text-white/80">
                  {c.b}
                </p>
                <ArrowUpRight
                  size={18}
                  className="mt-8 text-white/40 group-hover:text-unik-gold transition-colors"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      <BottomCTA
        eyebrow={t("common.getInTouch")}
        title={t("landing.ctaTitleBefore")}
        accent={t("landing.ctaTitleGold")}
        body={t("landing.ctaBody")}
        primary={{ label: t("common.contactUs"), to: "/contact" }}
        secondary={{ label: t("nav.investment"), to: "/investments" }}
      />

      <SiteFooter />
    </div>
  );
}
